<template>
  <div
    class="initiative-row"
    :class="{ editable, expanded, [`${viewType}-view`]: true, 'select-list-item': selectable }"
    :data-initiative-id="initiative.id"
    :data-initiative-row="true"
    @mouseenter="showOptions = true"
  >
    <div class="summary-wrapper d-inline-flex" @mouseup="onRowItemClick">
      <div class="cell row-controls border-0 d-none d-lg-flex">
        <div class="items">
          <div
            class="gripper-icon initiative-gripper"
            :class="{ hidden: !draggable || isLocal, [gripperIconClass]: true }"
          ></div>
          <collapse-caret
            class="flex-shrink-0 collapse-caret"
            :size="23"
            @click="toggleExpansionAndSelect"
            :expanded="expanded"
            v-if="canToggleExpansion"
          />
        </div>
      </div>
      <div class="summary row no-gutters flex-grow-1 flex-nowrap mw-0">
        <div class="cell col title-col d-flex pr-0" :class="{ new: isLocal }">
          <div tabindex="-1" class="tab-pin"></div>
          <!-- 🖕This being used to  improve tab-key experience. While user is navigating through the list using up and down key,
           tab should take focus on checkbox of current row
           -->

          <input
            class="mx-1"
            type="checkbox"
            :checked="initiative.isDoneAndClosed"
            @change="onIsDoneAndClosedChange"
          />
          <div
            class="d-flex flex-column flex-grow-1 pointer input-wrapper"
            :class="{ 'inline-edit': editable, 'position-relative': isSubmitting }"
          >
            <input
              type="text"
              class="flex-grow-1 initiative-title"
              v-model="initiativeTitle"
              @keyup="lastKey = $event.keyCode"
              @keyup.enter="saveOnEnter"
              placeholder="Enter initiative ([enter] to save, [esc] to cancel [tab] to edit priority and owners)"
              v-autofocus="editable"
              @blur="
                () =>
                  lastKey !== KEY_CODES.ENTER && lastKey !== KEY_CODES.ESCAPE && saveInitiative()
              "
              @focus="editable = true"
              data-prevent-blur="true"
              data-is-title-field="true"
              v-if="editable"
              v-no-capture
            />
            <div
              class="initiative-title-readview h-100 d-flex editable-text"
              :class="{ 'auto-show-action-icons': autoShowActionIcons }"
              @click="onTitleClick"
              v-else
            >
              <span
                class="initiative-title-value wrap-hyphenate"
                :class="{ bold: expanded }"
                v-no-capture
                >{{ initiative.title }}</span
              >
              <entity-popover
                :target="'initiative-expander-' + initiative.id"
                :entity-type="isTask ? ENTITY_TYPE.TASK : ENTITY_TYPE.INITIATIVE"
                :goalId="initiative.goalId"
                :krId="initiative.keyResultId"
                :org-id="initiative.organizationId"
                :parent-initiative-id="initiative.parentId"
                v-if="showParent"
              />
              <div class="icons-list align-self-baseline pl-2">
                <span
                  :data-comment-count="initiative.commentCount"
                  class="comment-badge-icon pointer"
                  title="comments"
                  @click.stop="onCommentBadgeClick"
                  :id="'initiative-comments-' + initiative.id"
                  v-if="!hideCommentIcon"
                >
                  <i class="comment-icon"></i>
                  <span v-if="initiative.commentCount > 0">{{ initiative.commentCount }}</span>
                </span>
                <span
                  :id="'initiative-edit-' + initiative.id"
                  class="edit-icon pointer d-none d-lg-inline-flex"
                  @click.stop="editInitiative"
                  ><i></i
                ></span>
                <span
                  :id="'initiative-expander-' + initiative.id"
                  class="expander-icon pointer d-none d-lg-inline-flex"
                  @click.stop="navigateToInitiativePage"
                  v-if="showDetailsPageNavigator"
                  ><i></i
                ></span>
                <action-tooltip
                  v-if="renderSecondLevelItems"
                  :target="'initiative-comments-' + initiative.id"
                  text="Comment"
                  :actions="viewType !== 'details' ? ['c'] : []"
                />
                <action-tooltip
                  v-if="renderSecondLevelItems"
                  :target="'initiative-edit-' + initiative.id"
                  text="Edit"
                  :actions="viewType !== 'details' ? ['e'] : []"
                />
                <action-tooltip
                  v-if="!showParent && renderSecondLevelItems && showDetailsPageNavigator"
                  :target="'initiative-expander-' + initiative.id"
                  text="Open"
                  :actions="['Shift', rightKey]"
                />
              </div>
            </div>
            <spinner size="xs" class="inline-edit-loader" v-if="isSubmitting" />
          </div>
        </div>
        <div class="initiative-items">
          <div
            v-for="field in sortedFields"
            :style="{ width: `${field.width}px` }"
            class="cell dropdown-cell text-center"
            :key="field.id"
          >
            <!-- Custom fields -->
            <template v-if="field.id">
              <initiative-custom-field-cell
                :initiative="initiative"
                :field="field"
                @update-initiative="$emit('update-initiative', $event)"
              />
            </template>
            <template v-else>
              <!-- Standard fields -->
              <due-date-field
                :initiative="initiative"
                @update="updateInitiative"
                v-if="field.name === INITIATIVE_STANDARD_FIELD_NAME.DUE_DATE"
              />
              <status-field
                :initiative="initiative"
                @change="updateInitiative"
                v-else-if="field.name === INITIATIVE_STANDARD_FIELD_NAME.STATUS"
              />
              <div
                class="cell-spacing pl-2"
                :class="getFieldAlignmentClass(field)"
                v-else-if="field.name === INITIATIVE_FE_COMPUTED_FIELD_NAME.GOAL"
                v-no-capture
              >
                {{ initiative.goalId ? goal && goal.title : '-' }}
              </div>
              <div
                class="cell-spacing pl-2"
                :class="getFieldAlignmentClass(field)"
                v-else-if="field.name === INITIATIVE_FE_COMPUTED_FIELD_NAME.KEY_RESULT"
                v-no-capture
              >
                {{ initiative.keyResultId ? kr && kr.title : '-' }}
              </div>
              <div
                class="cell-spacing"
                :class="getFieldAlignmentClass(field)"
                v-else-if="field.name === INITIATIVE_FE_COMPUTED_FIELD_NAME.PROJECT"
                v-no-capture
              >
                {{ initiative.projectId ? projectTitle : '-' }}
              </div>
              <b-dropdown
                toggle-class="dropdown-field d-flex flex-column align-items-center border-0"
                menu-class="menu-shadow"
                id="dropdown-left"
                variant="default"
                class="m-0"
                :lazy="true"
                v-else-if="field.name === INITIATIVE_STANDARD_FIELD_NAME.PRIORITY"
                no-caret
              >
                <template v-slot:button-content>
                  <span :class="IMPACT_BADGE_MAP[initiative.impact]">{{ impactLabel }}</span>
                </template>
                <b-dropdown-item
                  v-for="impact in impactOptions"
                  :key="impact.id"
                  @click="updateInitiative({ impact: impact.id })"
                  ><span class="text-prime text-medium-1">{{ impact.label }}</span>
                </b-dropdown-item>
              </b-dropdown>
              <div
                class="text-center border-right-0 owner pb-0 d-flex justify-content-center h-100"
                tabindex="0"
                @click="toggleOwnerSelector"
                @keydown.enter.stop
                @keydown.space.prevent
                @keyup.space.enter="openOwnerSelector"
                @keydown.esc.down.up="closeOwnerSelector"
                v-else-if="field.name === INITIATIVE_STANDARD_FIELD_NAME.OWNERS"
              >
                <ownership-short
                  :entity="initiative"
                  entity-type="initiative"
                  :length="22"
                  @change="onOwnershipChange"
                ></ownership-short>
              </div>
              <template v-else>
                <!-- Computed fields -->
                <initiative-computed-field-cell
                  class="h-100 d-flex justify-content-center computed-field-cell"
                  :field-name="field.name"
                  :initiative="initiative"
                  @open-comment-modal="showActivityModal($event)"
                />
              </template>
            </template>
          </div>
        </div>
      </div>
      <div class="initiative-options" v-if="!hideOptions">
        <initiative-options
          ref="initiativeOptions"
          v-if="!isLocal && showOptions"
          @open="navigateToInitiativePage"
          @edit="editInitiative"
          @editNote="editNoteViaMenu"
          :initiative="initiative"
          @moved-initiative="onInitiativeMove"
          @delete-initiatives="onInitiativeDelete"
          @initiative-is-closed-changed="onInitiativeIsClosedChanged"
          @awarded="$emit('awarded')"
        />
      </div>
    </div>
    <b-collapse v-model="expanded" class="expanded-section" v-if="renderSecondLevelItems">
      <div class="spacer"></div>
      <div class="note-wrapper flex-grow-1">
        <div class="section-wrapper">
          <div :style="{ width: `${noteWidth}px` }" v-no-capture>
            <custom-fields-view v-if="expanded && !isLocal" :initiative="initiative" />
            <div class="note-section">
              <initiative-sub-heading icon-class="note-icon" heading="Note">
                <span class="edit-icon ml-2" @click="editNoteViaInlineIcon"><i></i></span>
              </initiative-sub-heading>
              <initiative-note-editor
                :initiative="initiative"
                :errors="descriptionErrors"
                :is-loading="savingDescription"
                @save="saveDescription"
                @cancel="cancelDescriptionEditing"
                v-if="isDescriptionEditable"
              />
              <div
                class="description"
                :id="'description-' + uuid"
                v-else-if="initiative.description"
              >
                <component
                  class="w-100"
                  :bg-type="viewType === 'details' ? 'default' : 'primary'"
                  :is="expanded && viewType !== 'details' ? 'height-limiter' : 'div'"
                  max-height="200"
                  label="Show full note"
                >
                  <with-gallery
                    ref="gallery"
                    class="flex-grow-1"
                    v-html="description"
                  ></with-gallery>
                </component>
                <b-button
                  variant="link"
                  class="text-secondary text-medium px-0"
                  @click="isDescriptionEditable = true"
                  >Edit
                </b-button>
              </div>
              <b-button
                variant="link"
                class="text-secondary text-medium add-note-btn"
                @click="isDescriptionEditable = true"
                v-else
                >Add a note
              </b-button>
            </div>
            <tasks
              ref="tasks"
              class="mt-3"
              :selectable="selectableTasks"
              v-if="expanded && !isLocal && !isTask"
              :initiative-id="initiative.id"
              :org-id="orgId"
            />
          </div>
        </div>
      </div>
    </b-collapse>
    <max-initiatives-paywall-modal
      v-model="isPaywallModalShown"
      :org-id="orgId"
      v-if="isPaywallModalShown"
    />
    <initiative-modal
      v-model="isInlineActivityModalShown"
      v-bind="initiativeModalProps"
      :minimizable="false"
      v-if="isInlineActivityModalShown"
    />
  </div>
</template>

<script>
import { getCKEditorConfig } from '@/ckeditorConfig'
import ActionTooltip from '@/components/ActionTooltip'
import GiveAward from '@/components/award/GiveAward'
import CkEditor from '@/components/CkEditor'
import CollapseCaret from '@/components/CollapseCaret'
import CustomFieldsView from '@/components/initiative/CustomFieldsView.vue'
import EntityPopover from '@/components/EntityPopover'
import DatePicker from '@/components/form/DatePicker'
import HeightLimiter from '@/components/HeightLimiter'
import DueDateField from '@/components/initiative/DueDateField'
import InitiativeComputedFieldCell from '@/components/initiative/InitiativeComputedFieldCell'
import InitiativeCustomFieldCell from '@/components/initiative/InitiativeCustomFieldCell'
import InitiativeIcon from '@/components/initiative/InitiativeIcon'
import InitiativeModal from '@/components/initiative/InitiativeModal'
import InitiativeNoteEditor from '@/components/initiative/InitiativeNoteEditor.vue'
import InitiativeSubHeading from '@/components/initiative/InitiativeSubHeading'
import StatusField from '@/components/initiative/StatusField'
import InitiativeOptions from '@/components/InitiativeOptions'
import MaxInitiativesPaywallModal from '@/components/paywalls/MaxInitiativesPaywallModal.vue'
import Spinner from '@/components/Spinner'
import SubmitButtons from '@/components/SubmitButtons'
import WithGallery from '@/components/WithGallery'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import { DEBOUNCE_TIME, MIME_TYPE } from '@/constants/common'
import { CUSTOM_FIELD_TYPE } from '@/constants/customFields'
import { ENTITY_TYPE } from '@/constants/entities'
import errorCodes from '@/constants/errorCodes'
import events from '@/constants/events'
import {
  IMPACT,
  IMPACT_BADGE_MAP,
  INITIATIVE_COMPUTED_FIELD_NAME,
  INITIATIVE_FE_COMPUTED_FIELD_NAME,
  INITIATIVE_MANDATORY_FIELD,
  INITIATIVE_ROW,
  INITIATIVE_STANDARD_FIELD_NAME,
  INITIATIVE_STANDARD_FIELDS,
  InitiativeFieldType,
  STATUS
} from '@/constants/initiative'
import { KEY_CODES } from '@/constants/keyCodes'
import { DEFAULT_ERROR } from '@/constants/messages'
import { TOAST_TYPE } from '@/constants/toasts'
import ComponentIdMixin from '@/mixins/ComponentIdMixin'
import EventBusMixin from '@/mixins/EventBusMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import MediaQueryMixin from '@/mixins/MediaQueryMixin'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import { clearSelection } from '@/services/keymapService/keymapService'
import debounce from '@/utils/debounce'
import formatFreeText from '@/utils/formatFreeText'
import handleClickNavigation from '@/utils/handleClickNavigation'
import getIdFromCFPrefixedField from '@/utils/initiative/getIdFromCFPrefixedField'
import isFieldACustomField from '@/utils/initiative/IsFieldACustomField'
import isLocalId from '@/utils/initiative/isLocalId'
import { isCtrlOrShiftEvent } from '@/utils/kbEvents'
import multiClickHandler from '@/utils/multiClickHandler'
import { getInitiativePageRouteConfig } from '@/utils/navigationConfigs'
import prettifyKey from '@/utils/prettifyKey'
import showAPIError from '@/utils/showAPIError'
import showModal from '@/utils/showModal'
import sortInitiativeFields from '@/utils/sortInitiativeFields'
import URLify from '@/utils/URLify'
import { BButton, BCollapse, BDropdown, BDropdownItem } from 'bootstrap-vue'
import * as R from 'ramda'
import { mapActions } from 'vuex'
import { trackEvent } from '../../analytics'
import expansionState from '../../expansionState'
import OwnershipShort from '../OwnershipShort'

export default {
  name: INITIATIVE_ROW,
  components: {
    InitiativeNoteEditor,
    BDropdownItem,
    BDropdown,
    BCollapse,
    OwnershipShort,
    DatePicker,
    CkEditor,
    SubmitButtons,
    BButton,
    CustomFieldsView,
    ActionTooltip,
    InitiativeOptions,
    StatusField,
    HeightLimiter,
    GiveAward,
    DueDateField,
    InitiativeCustomFieldCell,
    WithGallery,
    InitiativeComputedFieldCell,
    Tasks: () => import(/* webpackChunkName: "tasks" */ '@/components/task/Tasks'),
    InitiativeSubHeading,
    InitiativeIcon,
    CollapseCaret,
    EntityPopover,
    Spinner,
    MaxInitiativesPaywallModal,
    InitiativeModal
  },
  mixins: [MembershipsMixin, EventBusMixin, MediaQueryMixin, FeaturesMixin, ComponentIdMixin],
  props: {
    initiative: {},
    orgId: {},
    krId: {},
    goalId: {},
    draggable: { default: true },
    showParent: { default: false },
    // list of all initiatives. Needed for navigation to next/previous initiative in initiative modal
    initiatives: { type: Array },
    showDetailsPageNavigator: {
      type: Boolean,
      default: true
    },
    canToggleExpansion: {
      type: Boolean,
      default: true
    },
    /*
    This tells where initaitive row is being used
    list - as list on KR page
    details - on initiative page
    child - as child of KR on goals/goal page
    * */
    viewType: {
      default: 'list' // list | details | child
    },
    /**
     * this shows the initiative in expanded mode on load
     * */
    expandOnLoad: Boolean,
    /**
     * Hide options menu
     */
    hideOptions: Boolean,
    hideCommentIcon: Boolean,
    /**
     * Action icons(that show up on hover) are shown upfront
     */
    autoShowActionIcons: Boolean,
    fieldsToDisplay: {
      type: Array,
      default: () => INITIATIVE_STANDARD_FIELDS.filter(x => x.name !== INITIATIVE_MANDATORY_FIELD)
    },
    /**
     * If true, initiative will be selectable via keyboard up and down keys
     */
    selectable: {
      type: Boolean,
      default: true
    },
    /**
     * If true, tasks will be selectable via keyboard up and down keys
     */
    selectableTasks: {
      type: Boolean,
      default: true
    },
    containerWidth: Number,
    editOnLoad: Boolean,
    activityModalMode: {
      // This controls if initiative modal should be minimizable
      default: 'minimizable', // options - normal, minimizable,
      type: String
    },
    gripperIconClass: {
      default: '',
      type: String
    }
  },
  data() {
    return {
      editable: !!this.editOnLoad, // tells if title is in edit mode
      isDescriptionEditable: false, // tells if description/note is in edit mode
      expanded:
        this.expandOnLoad ||
        !!(this.canToggleExpansion && expansionState.get('initiative', this.initiative.id)),
      lastKey: null,
      KEY_CODES,
      showDateSelector: false,
      IMPACT_BADGE_MAP,
      INITIATIVE_STANDARD_FIELD_NAME,
      INITIATIVE_FE_COMPUTED_FIELD_NAME,
      descriptionErrors: null, // contains errors occurred while editing description
      showCustomFieldsModal: false,
      ENTITY_TYPE,
      CUSTOM_FIELD_TYPE,
      renderSecondLevelItems: false,
      showOptions: false,
      rightKey: prettifyKey('<right>')[0],
      initiativeTitle: this.initiative.title,
      impactOptions: [IMPACT.low, IMPACT.medium, IMPACT.high],
      updatesBatch: {}, // when initiative creation is under progress, further changes to initiatives are stored here
      // KR selected while creating an initiative
      selectedKr: {
        id: this.initiative.keyResultId,
        goalId: this.initiative.goalId
      },
      savingDescription: false,
      isPaywallModalShown: false,
      isSubmitting: false,
      isInlineActivityModalShown: false,
      initiativeModalProps: {}
    }
  },
  computed: {
    isTask() {
      return !!this.initiative.parentId
    },
    sortedFields() {
      return sortInitiativeFields(this.fieldsToDisplay)
    },
    isLocal() {
      return isLocalId(this.initiative.id)
    },
    description() {
      return formatFreeText(
        this.initiative.organizationId,
        this.initiative.description,
        this.initiative.descriptionType
      )
    },
    goal() {
      return this.$store.getters.goalById(this.goalId)
    },
    kr() {
      return this.$store.getters.keyResultById(this.initiative.keyResultId)
    },
    projectTitle() {
      return this.$store.getters['project/projectById'](this.initiative.projectId)?.title
    },
    teamId() {
      return this.goal.teamId
    },
    team() {
      return this.$store.getters.teamById(this.teamId)
    },
    usersInScope() {
      let users = []
      if (this.goal) {
        if (this.goal.type === 'team' && this.team.isPrivate) {
          users = this.$store.getters.teamMemberships(this.team.id).map(x => x.user)
        } else {
          users = this.$store.getters.orgMemberships(this.goal.organizationId).map(x => x.user)
        }
      }
      return users
    },
    descriptionEditorConfig() {
      return getCKEditorConfig({
        startupFocus: true,
        users: this.usersInScope,
        height: 215,
        orgId: this.initiative.organizationId
      })
    },
    orgCustomFields() {
      return this.$store.state.orgCustomFields[this.orgId] || []
    },
    impactLabel() {
      const impact = this.impactOptions.find(R.propEq('id', this.initiative.impact))
      return impact.label
    },
    noteWidth() {
      const leftSpacing = 60
      const rightSpacing = 30
      return this.containerWidth - leftSpacing - rightSpacing
    },
    parentLocation() {
      return getInitiativePageRouteConfig({
        orgId: this.orgId,
        initiativeId: this.initiative.parentId
      })
    },
    isGloballySelected() {
      return this.$store.getters['bulkAction/selectedItems'].includes(this.initiative.id)
    }
  },
  methods: {
    ...mapActions([
      'createInitiative',
      'patchInitiative',
      'getInitiativeCustomFields',
      'getGoal',
      'getInitiative'
    ]),
    ...mapActions('project', ['getProject']),
    editInitiative() {
      setTimeout(() => {
        this.initiativeTitle = this.initiative.title
        this.editable = true
      }, 0)
    },
    getComputedFields() {
      return this.fieldsToDisplay
        .filter(x => x.fieldType === InitiativeFieldType.COMPUTED_FIELD)
        .map(x => x.name)
    },
    async setCustomField(initiative, fieldName, fieldValue) {
      const orgId = initiative.organizationId
      const fieldId = getIdFromCFPrefixedField(fieldName)
      const fields = this.$store.getters.customFieldDefinitions(orgId)
      const field = fields.find(field => field.id === fieldId)
      await this.$store.dispatch('setInitiativeCustomField', {
        orgId: orgId,
        initiativeId: initiative.id,
        field: field,
        value: fieldValue
      })
    },
    async saveInitiative() {
      const { impact, ownerId, coOwnerIds, id, projectId, sectionId } = this.initiative
      const krId = this.krId || this.selectedKr?.id
      const goalId = this.goalId || this.selectedKr?.goalId
      try {
        if (this.isLocal) {
          if (this.isSubmitting) return
          this.isSubmitting = true
          if (!this.initiativeTitle) {
            this.$emit('delete-initiatives', [this.initiative])
            return
          }
          const payload = {
            orgId: this.orgId,
            goalId,
            krId,
            projectId,
            sectionId,
            title: this.initiativeTitle,
            impact,
            ownerId,
            coOwnerIds
          }
          const { fieldName, fieldValue } = this.initiative.additionalField || {}
          if (fieldName && fieldValue && !isFieldACustomField(fieldName)) {
            payload[fieldName] = fieldValue
          }
          let initiative = await this.createInitiative(payload)
          if (fieldName && fieldValue && isFieldACustomField(fieldName)) {
            await this.setCustomField(initiative, fieldName, fieldValue)
          }
          this.editable = false
          const localId = this.initiative.id
          this.emitUpdateEvent(this.mergeWithLocalId({ localId }, initiative))
          if (!R.isEmpty(this.updatesBatch)) {
            const updatedInitiative = await this.updateInitiative(this.updatesBatch)
            if (updatedInitiative) {
              initiative = updatedInitiative
              this.updatesBatch = {}
            }
          }
          this.$emit('save-initiative', initiative)
        } else if (this.initiativeTitle !== this.initiative.title) {
          this.isSubmitting = true
          const payload = {
            orgId: this.orgId,
            initiativeId: id,
            data: {
              title: this.initiativeTitle
            },
            includeCustomFields: !!this.initiative.customFields,
            includeComputedFields: this.getComputedFields().join(',')
          }
          const initiative = await this.patchInitiative(payload)
          this.emitUpdateEvent(this.mergeWithLocalId(this.initiative, initiative))
        }
        this.editable = false
      } catch (e) {
        this.handleError(e)
      } finally {
        this.isSubmitting = false
      }
    },
    showPaywall() {
      this.isPaywallModalShown = true
    },
    handleError(e) {
      const isLimitReached = R.pathEq(
        ['response', 'data', 'code'],
        errorCodes.maxInitiativeCountLimitReached,
        e
      )
      if (isLimitReached) {
        this.showPaywall()
        return
      }
      const message =
        R.path(['response', 'data', 'detail', 'title', '0'], e) ||
        R.path(['response', 'data', 'message'], e) ||
        DEFAULT_ERROR
      this.eventBusEmit('ADD_TOAST', { message, type: TOAST_TYPE.ERROR })
    },
    emitUpdateEvent(initiative) {
      this.eventBusEmit(events.INITIATIVE_UPDATED, initiative)
    },
    async navigateToInitiativePage(event = {}, query = {}) {
      await this.getGoalIfNeeded()
      if (!this.editable) {
        const location = getInitiativePageRouteConfig({
          orgId: this.orgId,
          initiativeId: this.initiative.id,
          query
        })
        handleClickNavigation.call(this, event, location)
      }
    },
    changeIsDoneAndClosed: debounce(async function(value) {
      if (value !== this.initiative.isDoneAndClosed) {
        try {
          const payload = {
            orgId: this.orgId,
            initiativeId: this.initiative.id,
            data: { isClosed: value, status: value ? STATUS.completed.id : STATUS.notStarted.id },
            includeCustomFields: !!this.initiative.customFields,
            includeComputedFields: this.getComputedFields().join(',')
          }
          this.emitUpdateEvent({ ...this.initiative, isDoneAndClosed: value })
          const initiative = await this.patchInitiative(payload)
          this.emitUpdateEvent(initiative)
          this.$emit('change-is-closed', value)
        } catch (e) {
          showAPIError(e)
          this.emitUpdateEvent({ ...this.initiative, isDoneAndClosed: !value })
        }
      }
    }, DEBOUNCE_TIME),
    onIsDoneAndClosedChange: function(event) {
      this.changeIsDoneAndClosed(event.target.checked)
    },
    URLify,
    cancelInitiativeEdit() {
      // This function is being used for cancelling the editing on press of esc key
      this.editable = false
      this.initiativeTitle = this.initiative.title
      if (this.isLocal) {
        this.$emit('delete-initiatives', [this.initiative])
        this.selectLastElement()
      }
      this.lastKey = KEY_CODES.ESCAPE
      if (this.editable) {
        this.editable = false
        document.activeElement.blur()
      }
    },
    selectLastElement() {
      if (this.selectable) {
        const parentElement = this.$el.parentElement
        this.$nextTick(function() {
          const rows = parentElement.querySelectorAll('.select-list-item')
          const last = rows[rows.length - 1]
          last && last.classList.add('selected')
        })
      }
    },
    onRowItemClick(event) {
      if (!isCtrlOrShiftEvent(event)) {
        this.resetAndSelectInitiative()
      }
    },
    resetAndSelectInitiative() {
      this.$nextTick(function() {
        clearSelection() // This will unselect the previously selected item
        this.selectCurrentInitiative()
      })
    },
    selectCurrentInitiative() {
      if (this.selectable) {
        this.$el.classList.add('selected')
      }
    },
    unselectCurrentInitiative() {
      if (this.selectable) {
        this.$el.classList.remove('selected')
      }
    },
    saveOnEnter() {
      this.saveInitiative()
    },
    /**
     * this checks if objA is subset of objB
     * @param objA
     * @param objB
     */
    isSubset(objA, objB) {
      return Object.keys(objA).every(prop => objA[prop] === objB[prop])
    },
    trackEvent(data) {
      let action
      if (data.impact) {
        action = EVENT_ACTION.SET_INITIATIVE_PRIORITY
      } else if (data.status) {
        action = EVENT_ACTION.SET_INITIATIVE_STATUS
      } else if (data.dueDate) {
        action = EVENT_ACTION.SET_INITIATIVE_DUE_DATE
      }
      if (action) {
        trackEvent(EVENT_CATEGORY.ACTIVITY, action)
      }
    },
    async updateInitiative(data) {
      if (this.isLocal) {
        this.updatesBatch = { ...this.updatesBatch, ...data }
      } else if (!this.isSubset(data, this.initiative)) {
        // prevent calling API when nothing changed
        try {
          const payload = {
            orgId: this.orgId,
            initiativeId: this.initiative.id,
            data,
            includeCustomFields: !!this.initiative.customFields,
            includeComputedFields: this.getComputedFields().join(',')
          }
          setTimeout(() => this.trackEvent(data)) // adding setTimeout so that PATCH API gets fired earlier
          const initiative = await this.patchInitiative(payload)
          this.emitUpdateEvent(initiative)
          return initiative
        } catch (e) {
          showAPIError(e)
        }
      }
    },
    openOwnerSelector(event) {
      event.target.firstChild.__vue__.editing = true
    },
    closeOwnerSelector(event) {
      event.target.firstChild.__vue__.editing = false
    },
    toggleOwnerSelector(event) {
      const isEditing = event.currentTarget.firstChild.__vue__.editing
      event.currentTarget.firstChild.__vue__.editing = !isEditing
    },
    toggleExpansionAndSelect({ forceExpand = false } = {}) {
      if (forceExpand) {
        this.expanded = true
      } else if (this.canToggleExpansion) {
        this.expanded = !this.expanded
      }
      this.resetAndSelectInitiative()
    },
    editNoteViaMenu() {
      this.toggleExpansionAndSelect({ forceExpand: true })
      this.isDescriptionEditable = true
      trackEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.EDIT_NOTE, EVENT_NAME.INITIATIVE_MENU)
    },
    editNoteViaInlineIcon() {
      this.isDescriptionEditable = true
      trackEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.EDIT_NOTE, EVENT_NAME.INLINE_ICON)
    },
    onTitleClick(event) {
      if (isCtrlOrShiftEvent(event)) {
        // don't handle click, as this is multi-selection flow
        return
      }
      multiClickHandler({
        onSingleClick: () => {
          this.isMobileMode && this.navigateToInitiativePage()
        },
        onDoubleClick: () => {
          this.editInitiative()
        }
      })
    },
    async saveDescription({ description, notifySubscribers }) {
      if (description !== this.initiative.description) {
        try {
          this.savingDescription = true
          const payload = {
            orgId: this.orgId,
            initiativeId: this.initiative.id,
            data: { description, descriptionType: MIME_TYPE.HTML },
            includeCustomFields: !!this.initiative.customFields,
            includeComputedFields: this.getComputedFields().join(','),
            notifySubscribers
          }
          const latestInitiative = await this.patchInitiative(payload)
          const initiative = this.mergeWithLocalId(this.initiative, latestInitiative)
          this.emitUpdateEvent(initiative)
          this.isDescriptionEditable = false
          this.descriptionErrors = null
        } catch (e) {
          let errors = R.path(['response', 'data', 'detail', 'description'], e)
          if (!errors) {
            const message = R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
            errors = [message]
          }
          this.descriptionErrors = errors
        }
        this.savingDescription = false
      } else {
        this.isDescriptionEditable = false
      }
    },
    onComment({ value: initiative, type }) {
      if (type === 'initiative' && initiative.id === this.initiative.id) {
        this.refetchInitiative({ initiativeId: this.initiative.id })
      }
    },
    onCommentBadgeClick() {
      this.showActivityModal()
      trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.ACTIVITY_MODAL, EVENT_NAME.INITIATIVE)
    },
    showActivityModal({ focusAddCommentOnLoad, eventId } = {}) {
      this.initiativeModalProps = {
        initiatives: this.initiatives,
        initiative: this.initiative,
        focusAddCommentOnLoad,
        focusEventIdOnLoad: eventId
      }
      if (this.activityModalMode === 'minimizable') {
        showModal({
          component: InitiativeModal,
          id: `${ENTITY_TYPE.INITIATIVE}-${this.initiative.id}`,
          props: {
            show: true,
            ...this.initiativeModalProps
          }
        })
      } else {
        this.isInlineActivityModalShown = true
      }
    },
    async refreshGoal() {
      const showGoalOrKRField = this.fieldsToDisplay.some(x =>
        [
          INITIATIVE_FE_COMPUTED_FIELD_NAME.GOAL,
          INITIATIVE_FE_COMPUTED_FIELD_NAME.KEY_RESULT
        ].includes(x.name)
      )
      if (showGoalOrKRField && this.initiative.goalId && !this.goal) {
        await this.$store.dispatch('getGoal', {
          orgId: this.orgId,
          goalId: this.initiative.goalId
        })
      }
    },
    refreshProject() {
      const hasProjectField = !!this.fieldsToDisplay.find(
        field => field.name === INITIATIVE_COMPUTED_FIELD_NAME.PROJECT
      )
      if (hasProjectField && this.initiative.projectId && !this.projectTitle) {
        this.getProject({
          orgId: this.initiative.organizationId,
          projectId: this.initiative.projectId
        })
      }
    },
    cancelDescriptionEditing() {
      this.isDescriptionEditable = false
      this.descriptionErrors = null
    },
    onInitiativeUpdate(initiative) {
      if ([initiative.id, initiative.localId].includes(this.initiative.id)) {
        this.$emit('update-initiative', {
          ...this.initiative,
          ...initiative
        })
      }
    },
    showAwardModal() {
      if (!this.showOptions) {
        this.showOptions = true
      }
      this.$nextTick(() => {
        this.$refs.initiativeOptions.showAwardModal()
      })
    },
    onOwnershipChange(updatedInitiative) {
      const initiative = this.mergeWithLocalId(this.initiative, updatedInitiative)
      this.emitUpdateEvent(initiative)
    },
    onInitiativeDelete(payload) {
      this.$emit('delete-initiatives', payload)
    },
    onInitiativeMove(payload) {
      this.$emit('moved-initiative', payload)
    },
    onInitiativeIsClosedChanged(initiative) {
      this.emitUpdateEvent(initiative)
      this.$emit('change-is-closed', initiative.isClosed)
    },
    async refetchInitiative({ initiativeId, forceFetch }) {
      if (forceFetch || this.initiative.id === initiativeId) {
        const initiative = await this.getInitiative({
          orgId: this.orgId,
          initiativeId,
          includeCustomFields: true,
          includeStandardFields: this.getComputedFields().join(',')
        })
        this.emitUpdateEvent({ ...this.initiative, ...initiative })
      }
    },
    async refetchCurrentInitiative() {
      this.getInitiative({
        orgId: this.orgId,
        initiativeId: this.initiative.id,
        includeCustomFields: true,
        includeStandardFields: this.getComputedFields().join(',')
      })
    },
    createImageGallery() {
      this.$refs.gallery?.create()
    },
    mergeWithLocalId(source, target) {
      if (source.id && target.id && source.id !== target.id) {
        throw new Error('Source and target are different')
      }
      return { localId: source.localId, ...target }
    },
    async getGoalIfNeeded() {
      if (!this.goal && this.initiative.goalId) {
        await this.getGoal({
          orgId: this.initiative.organizationId,
          goalId: this.initiative.goalId
        })
      }
    },
    /**
     * This is getting used by InitiativesKeyboardNavMixins. It is used to trigger initiative delete confirmation modal
     */
    triggerDeleteInitiative() {
      if (!this.showOptions) {
        this.showOptions = true
      }
      this.$nextTick(() => {
        this.$refs.initiativeOptions.showDeleteModal()
      })
    },
    getFieldAlignmentClass(field) {
      const map = {
        left: 'text-left pl-2',
        center: 'text-center'
      }
      return map[field.align]
    }
  },
  mounted() {
    if (this.isLocal) {
      setTimeout(() => (this.editable = true), 0)
    } else {
      this.refreshGoal()
      this.refreshProject()
    }
    if (this.expanded) {
      this.renderSecondLevelItems = true
    } else {
      setTimeout(() => (this.renderSecondLevelItems = true), 500)
    }
    this.listenToEventBus(events.COMMENT_ADDED, this.onComment)
    this.listenToEventBus(events.COMMENT_DELETED, this.onComment)
    this.listenToEventBus(events.INITIATIVE_UPDATED, this.onInitiativeUpdate)
    this.listenToEventBus(events.INITIATIVE_CUSTOM_FIELD_VALUE_UPDATED, this.refetchInitiative)
    this.listenToEventBus(events.INITIATIVE_CUSTOM_FIELD_FORMULA_UPDATED, () =>
      setTimeout(this.refetchCurrentInitiative, 1000)
    )
  },
  watch: {
    initiative(newInitiative, oldInitiative = {}) {
      if (newInitiative.id !== oldInitiative.id) {
        this.refreshGoal()
      }
      if (newInitiative.description !== oldInitiative.description)
        this.$nextTick(this.createImageGallery)
    },
    editable() {
      this.resetAndSelectInitiative()
      if (this.editable) {
        this.lastKey = null
      }
    },
    expanded(isExpanded) {
      expansionState.set('initiative', this.initiative.id, isExpanded)
      if (isExpanded) {
        setTimeout(this.createImageGallery, 500)
      }
    },
    fieldsToDisplay() {
      this.refreshGoal()
      this.refreshProject()
    },
    isGloballySelected(isSelected) {
      if (isSelected) {
        this.selectCurrentInitiative()
      } else {
        this.unselectCurrentInitiative()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/drag';
@import '~@/styles/styleConfigurations';
@import '~@/styles/errors';
@import '~@/styles/components/badge';
@import '~@/styles/components/input';

$titleColWidth: 350px;
$cellTopSpacing: 7px;

.initiative-row {
  font-size: 0.875rem;
  display: inline-flex;
  flex-direction: column;
  min-width: 100%;

  .summary-wrapper {
    &:hover {
      .gripper-icon {
        opacity: 0.2;

        &:hover {
          opacity: 1;
        }
      }
    }
  }

  .cell-spacing {
    padding-top: $cellTopSpacing;
  }

  .row-controls {
    @include sticky(false, 0);

    display: flex;
    justify-content: flex-end;
    background-color: $documentColor;
    width: 60px;

    .items {
      display: flex;
      align-self: baseline;
      align-items: center;
      transform: translateY(5px);
    }
  }

  .gripper-icon {
    opacity: 0;
    cursor: move;
    transition: opacity ease-in 200ms;
    margin-top: 1px;
  }

  .summary {
    transition: all ease-in 150ms;
    border-radius: 3px;
  }

  .impact-high {
    background-color: #d3f3ee;
  }

  .impact-medium {
    background-color: #f7debb;
  }

  .expanded-section {
    margin-top: 0px;
    display: flex;

    .section-wrapper {
      padding: 18px 0 10px;

      .description {
        word-break: break-word;
      }
    }

    &.collapsing {
      opacity: 0;
    }
  }

  &.list-view,
  &.child-view {
    .section-wrapper {
      border-radius: 3px;
      background-color: $contentBGPrimary;
    }

    .note-wrapper {
      padding: 3px 30px 20px 0;
    }

    .spacer {
      @include sticky(false, 0, 1);

      flex-basis: 60px;
      flex-shrink: 0;
      background-color: $documentColor;
    }

    .initiative-items /deep/ .due-date-field {
      padding-top: 7px;
    }
  }

  .title-col {
    @include sticky(false, 60px);

    min-width: $titleColWidth;
    flex-grow: 1;

    input[type='checkbox'] {
      height: 1.5rem;
      margin-top: 6px;
      flex-shrink: 0;
      width: 13px;
    }
  }

  .cell {
    flex-shrink: 0;
    background-color: $documentColor;

    &:first-child {
      .input-wrapper {
        min-width: 0;

        input {
          padding: 5px 4px;
          margin: 2px 0;
        }
      }
    }

    .inline-edit-loader {
      position: absolute;
      right: 10px;
      top: 10px;
    }

    &:not(:last-child) {
      border-right: 1px solid #edeff1;
    }

    &.dropdown-cell .dropdown {
      @include dimension(100%, 100%);

      button {
        flex-grow: 0;
      }
    }

    .badge-light {
      font-size: 12px;
      font-weight: 400;
      padding-right: 10px;
      padding-left: 10px;
      color: #8a8a8a;
      text-transform: capitalize;
    }

    &:first-child {
      input[type='text'] {
        border: none;
        background: transparent;

        &:focus {
          outline: none;
        }

        &[disabled='disabled'] {
          cursor: pointer;
        }
      }
    }

    border-top: 1px solid #edeff1;

    .impact-toggle {
      background: #fff;
      color: #8a8a8a;
      font-size: 11px;
      padding: 4px 12px;
      text-transform: capitalize;
    }
  }

  &:not(.editable) {
    input.initiative-title {
      pointer-events: none;
    }
  }

  &.editable {
    .input-wrapper {
      .collapse-caret {
        display: none;
      }

      /deep/ .initiative-options-button {
        display: none;
      }

      .cell {
        &:first-child.new {
          input[type='checkbox'] {
            display: none;
          }

          &:before {
            content: ' ';
            height: 12px;
            width: 12px;
            margin: 6px 8px 6px 0;
            display: inline-block;
            background: #c4c4c4;
            border-radius: 3px;
          }
        }

        &.owner {
          position: relative;
        }
      }
    }
  }

  &:hover .initiative-options {
    /deep/ .initiative-options-button {
      display: initial;
    }
  }

  &.select-list-item.selected .cell {
    background: $selectionColor;
  }

  &.select-list-item.selected {
    + .initiative-row .cell,
    .cell {
      border-top-color: transparent;
    }

    /deep/ .editable-text:hover {
      background-color: $brandQuaternary;
    }
  }

  &.selected,
  &:hover {
    .collapse-caret {
      opacity: 1;
    }
  }

  .submit-action {
    display: flex;

    &.invisible {
      visibility: hidden;
    }
  }
}

.tab-pin {
  align-self: center;
}

/deep/ .dropdown-field,
.owner,
.computed-field-cell {
  padding-top: $cellTopSpacing;
}

.initiative-row.details-view {
  &.select-list-item.selected .summary {
    background-color: inherit;
  }

  .note-wrapper {
    margin: 0;
  }

  .icons-list {
    padding-top: 8px;
  }

  .row-controls {
    width: auto;
  }

  .note-wrapper p {
    padding-left: 0;
    border-left: none;
  }

  .summary-wrapper {
    padding: 0;
  }

  .edit-menu {
    padding-top: 10px;
  }

  .cell {
    border-top: none;

    input,
    .initiative-title-readview {
      padding-bottom: 7px;
      font-size: 21px;
      font-weight: 600;
    }

    .input-wrapper {
      padding-top: 0;
      padding-bottom: 0;
    }

    input[type='checkbox'] {
      margin-top: 12px;
      width: 15px;
    }
  }

  .initiative-options-menu {
    right: -8px;
  }

  .initiative-items {
    /deep/ .dropdown-field,
    /deep/ .due-date-field,
    .owner {
      padding-top: 15px;
    }
  }

  /deep/ .due-date-field .placeholder {
    opacity: 1;
  }

  .inline-edit-loader {
    top: 16px;
  }
}

.initiative-row.closed {
  input,
  .initiative-title-value {
    color: $secondary;
  }

  /deep/ .badge,
  /deep/ .selected-status,
  /deep/ .user-photo-container {
    opacity: 0.5;
  }
}

.kr-icon {
  transform: scale(0.7);
  transform-origin: 12px 12px;
  filter: grayscale(1);
}

.edit-icon,
.expander-icon,
.comment-badge-icon {
  transition: opacity ease-out 200ms;
}

.initiative-title-readview {
  padding: $cellTopSpacing 4px 5px 4px;

  &:not(.auto-show-action-icons):hover {
    .edit-icon,
    .expander-icon,
    .comment-badge-icon {
      opacity: 1;
    }
  }
}

.comment-icon {
  margin-right: 2px;
}

.initiative-title-readview:not(.auto-show-action-icons) {
  .edit-icon,
  .expander-icon {
    opacity: 0;
  }
}

.initiative-items {
  display: contents;
}

.add-note-btn {
  padding-top: 0;
  padding-left: 24px;
}

.icons-list {
  margin-left: 8px;
  min-height: 23px;
  display: flex;
  align-items: center;
}

.icons-list > * {
  margin-right: 2px;
}

.description {
  position: relative;
  padding-left: 26px;
}

.description .edit-icon {
  position: absolute;
  right: -20px;
  top: 2px;
}

/deep/ .select-impact,
/deep/ .select-status,
.owner {
  padding-top: $cellTopSpacing;
}

.sub-heading {
  font-weight: 600;
  margin-bottom: 10px;
}

.note-section {
  margin: 0 28px;
  min-height: 65px;
}

.note-section .edit-icon {
  opacity: 0;
  @include transition(opacity);
}

.note-section:hover .edit-icon {
  opacity: 1;
}

@mixin mobileView() {
  .summary {
    display: flex;
    flex-direction: column;
  }
  .initiative-items {
    display: flex;
    margin-left: 29px;
    border-bottom: 1px solid $borderPrimary;
    padding-bottom: 12px;
  }
  .cell {
    border-right: none !important;
  }
}

@include media-breakpoint-down(md) {
  .initiative-row .title-col {
    left: 0;
    min-width: $titleColWidth - 100px;
  }
  .initiative-options {
    display: none;
  }

  .details-view {
    @include mobileView;
  }
  .spacer {
    display: none;
  }
  .list-view,
  .child-view {
    .note-wrapper {
      padding-right: 0 !important;
    }
  }
}

@include media-breakpoint-up(lg) {
  .initiative-options {
    width: 36px;
    flex-shrink: 0;
  }
  .comment-badge-icon[data-comment-count='0'] {
    opacity: 0;
  }
}
</style>

<style lang="scss">
.initiative-row {
  .initiative-title {
    color: #232323;
  }

  .owner {
    button {
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 100%;
      background: transparent;

      &:focus,
      &[aria-describedby] {
        box-shadow: 0 0 0 0.2rem rgba(38, 143, 255, 0.5);
      }
    }

    &:focus {
      z-index: 1;
      box-shadow: inset 0 0 0 0.2rem rgba(38, 143, 255, 0.5);
      background: #edf8fe;
      outline: none;
    }
  }

  .initiative-items .dropdown {
    position: initial;

    .dropdown-field {
      padding-left: 8px;
      padding-right: 8px;
      border-radius: 0;

      &:hover {
        color: inherit;
      }

      &:focus {
        box-shadow: inset 0 0 0 0.2rem rgba(38, 143, 255, 0.5);
        background: #edf8fe;

        button {
          border-color: transparent;
        }
      }

      button:focus {
        box-shadow: none;
        border-color: transparent;
      }
    }

    &.show .dropdown-field {
      box-shadow: inset 0 0 0 0.2rem rgba(38, 143, 255, 0.5);
      color: inherit;

      button {
        border-color: transparent;
      }
    }
  }
}
</style>
